import React from 'react';
import { Table, Card, Typography, Divider, Button, Row, Col } from 'antd';
import { CommonTable, formatPriceString, SkeletonHOC } from 'common';
import { RESULT_COLUMN_NAMES } from '../UserUsageReport.constants';
import { CustomerUsageReport } from '../models';
import { useDispatch } from 'react-redux';
import { exportReportsToExcel } from '..';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';
const { Column } = Table;
const { Title, Text } = Typography;

interface Props {
  reports: CustomerUsageReport[];
  searchCriteria: string;
}

const getRender =
  (columnName: string) => (text: string, report: CustomerUsageReport) => {
    switch (columnName) {
      case RESULT_COLUMN_NAMES.totalAmount.dataIndex:
        return <>{formatPriceString(report.amount)}</>;
      case RESULT_COLUMN_NAMES.searchParameters.dataIndex:
        // set whitespace to pre-wrap so that search parameters display line breaks and whitespace as returned from database
        return <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>;
      default:
        return text;
    }
  };

const UserUsageReportResult: React.FC<Props> = ({
  reports,
  searchCriteria,
}) => {
  const dispatch = useDispatch();
  const count = reports.length;
  const resultsNoun = count === 1 ? 'result' : 'results';
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const exportReportsToExcelClick = () => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            exportReportsToExcel(reports, searchCriteria, response.accessToken),
          );
      });
    }
  };

  return (
    <>
      <Divider dashed />
      <Card size="small">
        <Row type="flex" justify="space-between" align="middle">
          <Col span={24}>
            <Title>Usage Report</Title>
          </Col>
        </Row>
        <br />
        <Row type="flex" justify="space-between" align="middle">
          <Col span={20}>
            <Title level={4}>Search Criteria: {searchCriteria}</Title>
            <Text>
              Your search has returned {count} {resultsNoun}
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon="file-excel"
              onClick={() => exportReportsToExcelClick()}
            >
              Export as Excel
            </Button>
          </Col>
        </Row>
        <Divider dashed />
        <CommonTable items={reports} rowKey={'index'}>
          {Object.values(RESULT_COLUMN_NAMES).map(
            ({ title, dataIndex, width }, index) => (
              <Column
                width={width}
                {...{ title, dataIndex, render: getRender(dataIndex) }}
                key={index}
              />
            ),
          )}
        </CommonTable>
      </Card>
    </>
  );
};

export default SkeletonHOC({ initProp: 'reports', rows: 8 })(
  UserUsageReportResult,
);
